import { useEffect, useState } from "react";

import {
  ConfirmDialog,
  confirmDialog,
  ConfirmDialogProps,
} from "primereact/confirmdialog";
import { Button } from "primereact/button";
import { BehaviorSubject } from "rxjs";

import { i18n, LocalizedText, resolveLocalizedText, translate } from "./shared/language";

const props = new BehaviorSubject<ConfirmDialogProps | null>(null);

export const ConfirmDialogService = {
  setProps: (data: ConfirmDialogProps) => props.next({
    ...data,
    // Existing callers still pass FE-owned Vietnamese labels directly. Keep
    // messages untouched because they can be supplied by the backend.
    header: typeof data.header === "string" ? translate(data.header) : data.header,
    acceptLabel: typeof data.acceptLabel === "string" ? translate(data.acceptLabel) : data.acceptLabel,
    rejectLabel: typeof data.rejectLabel === "string" ? translate(data.rejectLabel) : data.rejectLabel,
  }),
  getProps: () => props.asObservable(),
  confirm: (header: LocalizedText, message: LocalizedText, accept: () => void) => {
    props.next({
      message: resolveLocalizedText(message),
      header: resolveLocalizedText(header),
      icon: "pi pi-info-circle",
      acceptClassName: "p-button-danger",
      acceptLabel: i18n.t("common.confirm"),
      rejectLabel: i18n.t("common.reject"),
      accept,
    });
  },
  alert: (header: string, message: any, accept: () => void) => {
    props.next({
      message,
      header,
      icon: "pi pi-info-circle text-orange-400",
      footer: ({ reject }) => (
        <div className="flex justify-content-between align-items-center">
          <span>{i18n.t("common.continueSaving")}</span>
          <div>
            <Button
              label={i18n.t("common.reject")}
              className="p-button-text"
              onClick={reject}
            />
            <Button
              label={i18n.t("common.confirm")}
              onClick={() => {
                accept();
                reject();
              }}
            />
          </div>
        </div>
      ),
    });
  },
  confirmCancel: (accept: () => void) => {
    props.next({
      message: i18n.t("common.cancelRecordQuestion"),
      header: i18n.t("common.cancelRecord"),
      icon: "pi pi-info-circle",
      acceptClassName: "p-button-danger",
      acceptLabel: i18n.t("common.cancel"),
      rejectLabel: i18n.t("common.reject"),
      accept,
    });
  },
  confirmDelete: (accept: () => void) => {
    props.next({
      message: i18n.t("common.deleteRecordQuestion"),
      header: i18n.t("common.deleteData"),
      icon: "pi pi-info-circle",
      acceptClassName: "p-button-danger",
      acceptLabel: i18n.t("common.delete"),
      rejectLabel: i18n.t("common.reject"),
      accept,
    });
  },
  confirmSign: (accept: () => void) => {
    props.next({
      message: i18n.t("common.signAndSendQuestion"),
      header: i18n.t("common.signAndSend"),
      icon: "pi pi-info-circle",
      acceptClassName: "p-button-danger",
      acceptLabel: i18n.t("common.confirm"),
      rejectLabel: i18n.t("common.reject"),
      accept,
    });
  },
  confirmSignStatement: (accept: () => void) => {
    props.next({
      message: i18n.t("common.signedStatementWarning"),
      header: i18n.t("common.notice"),
      icon: "pi pi-info-circle",
      acceptClassName: "p-button-danger",
      acceptLabel: i18n.t("common.confirm"),
      rejectLabel: i18n.t("common.reject"),
      accept,
    });
  },
  showError: (errorCode: string, errors: string) => {
    props.next({
      message: errors,
      header: i18n.t("common.errorCode", { code: errorCode }),
      icon: "pi pi-times-circle text-orange-400",
      acceptLabel: i18n.t("common.close"),
      rejectClassName: "hidden",
    });
  },
  showInvalid: (message: string) => {
    props.next({
      message,
      header: i18n.t("common.error"),
      icon: "pi pi-times-circle text-orange-400",
      acceptLabel: i18n.t("common.close"),
      rejectClassName: "hidden",
    });
  },
  confirmResetPermision: (accept: () => void) => {
    props.next({
      message: i18n.t("common.restorePermissionQuestion"),
      header: i18n.t("common.restorePermission"),
      icon: "pi pi-info-circle",
      acceptClassName: "p-button-danger",
      acceptLabel: i18n.t("common.confirm"),
      rejectLabel: i18n.t("common.reject"),
      accept,
    });
  },
  confirmSetPermision: (accept: () => void) => {
    props.next({
      message: i18n.t("common.permissionQuestion"),
      header: i18n.t("system.permissionGroups"),
      icon: "pi pi-info-circle",
      acceptClassName: "p-button-danger",
      acceptLabel: i18n.t("common.confirm"),
      rejectLabel: i18n.t("common.reject"),
      accept,
    });
  },
  confirmReloadAuthorisedCode: (accept: () => void) => {
    props.next({
      message: i18n.t("common.taxAuthorityCodeQuestion"),
      header: i18n.t("common.getTaxAuthorityCode"),
      icon: "pi pi-info-circle",
      acceptClassName: "p-button-danger",
      acceptLabel: i18n.t("common.confirm"),
      rejectLabel: i18n.t("common.reject"),
      accept,
    });
  },
  confirmChangeTax: (accept: () => void) => {
    props.next({
      message: i18n.t("common.changeTaxQuestion"),
      header: i18n.t("common.changeTax"),
      icon: "pi pi-info-circle",
      acceptClassName: "p-button-danger",
      acceptLabel: i18n.t("common.yes"),
      rejectLabel: i18n.t("common.no"),
      accept,
    });
  },
  confirmUpdateCompanyInfo: (accept: () => void) => {
    props.next({
      message: i18n.t("common.updateSellerInfoQuestion"),
      header: i18n.t("common.updateSellerInfo"),
      icon: "pi pi-info-circle",
      acceptClassName: "p-button-danger",
      acceptLabel: i18n.t("common.yes"),
      rejectLabel: i18n.t("common.cancel"),
      accept,
    });
  },
  confirmAdjustMachineInvoice: (accept: () => void, reject?: () => void) => {
    props.next({
      style: { width: "450px" },
      message: i18n.t("common.machineInvoiceWarning"),
      header: i18n.t("common.warning"),
      icon: "pi pi-info-circle",
      acceptClassName: "p-button-danger",
      acceptLabel: i18n.t("common.yes"),
      rejectLabel: i18n.t("common.no"),
      accept,
      reject,
    });
  },
};

export const AppConfirmDialog = () => {
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const subscription = ConfirmDialogService.getProps().subscribe((nextProps) => {
      if (nextProps) {
        confirmDialog(nextProps);
      }
    });
    return () => subscription.unsubscribe();
  }, []);

  return <ConfirmDialog visible={visible} onHide={() => setVisible(false)} />;
};
